// Pure math + parse helpers for the bug-report dock position. Split out
// of `useBugReportDockPosition.ts` so the hook stays under the file-size
// hygiene cap and so unit tests can exercise the math without touching
// React. See docs/planning/bug_report_placement_v2.md.

export const BUG_REPORT_DOCK_POSITION_STORAGE_KEY = "bb-bug-report-dock-position";

/** Inset from the viewport bottom-right corner used for the default position. */
export const DEFAULT_INSET_PX = 24;

/**
 * Below this width/height delta (in px) we treat the stored viewport size as
 * "close enough" to the current one and skip the rescale step. Avoids jitter
 * from minor viewport oscillation (e.g. iOS URL bar collapse/expand).
 */
export const VIEWPORT_DRIFT_TOLERANCE_PX = 32;

export type BugReportDockPosition = {
  /** Top-left x in viewport coords. */
  x: number;
  /** Top-left y in viewport coords. */
  y: number;
};

export type StoredBugReportDockPosition = BugReportDockPosition & {
  v: 1;
  /** Viewport width at save time — used to reproject on a different display. */
  vw: number;
  /** Viewport height at save time. */
  vh: number;
};

/**
 * Clamp the (x, y) top-left so the pill (of `elementW` × `elementH`) stays
 * fully on-screen. Negative or out-of-range viewport sizes degrade to a
 * no-op clamp at (0, 0).
 */
export function clampPosition(args: {
  x: number;
  y: number;
  viewportW: number;
  viewportH: number;
  elementW: number;
  elementH: number;
}): BugReportDockPosition {
  const { x, y, viewportW, viewportH, elementW, elementH } = args;
  const maxX = Math.max(0, viewportW - elementW);
  const maxY = Math.max(0, viewportH - elementH);
  return {
    x: Math.min(Math.max(x, 0), maxX),
    y: Math.min(Math.max(y, 0), maxY),
  };
}

/**
 * Reproject a stored (x, y) saved at `(stored.vw, stored.vh)` onto the
 * current viewport. If the stored viewport is missing/zero, returns the
 * stored coords unchanged (let `clampPosition` handle the cleanup).
 */
export function scalePosition(args: {
  stored: StoredBugReportDockPosition;
  currentVw: number;
  currentVh: number;
}): BugReportDockPosition {
  const { stored, currentVw, currentVh } = args;
  if (stored.vw <= 0 || stored.vh <= 0) {
    return { x: stored.x, y: stored.y };
  }
  const vwClose = Math.abs(stored.vw - currentVw) <= VIEWPORT_DRIFT_TOLERANCE_PX;
  const vhClose = Math.abs(stored.vh - currentVh) <= VIEWPORT_DRIFT_TOLERANCE_PX;
  if (vwClose && vhClose) {
    return { x: stored.x, y: stored.y };
  }
  return {
    x: (stored.x / stored.vw) * currentVw,
    y: (stored.y / stored.vh) * currentVh,
  };
}

/**
 * Default position = bottom-right minus DEFAULT_INSET_PX padding. Matches the
 * legacy `fixed bottom-4 right-4` placement visually.
 */
export function defaultPosition(args: {
  viewportW: number;
  viewportH: number;
  elementW: number;
  elementH: number;
}): BugReportDockPosition {
  const { viewportW, viewportH, elementW, elementH } = args;
  return {
    x: Math.max(0, viewportW - elementW - DEFAULT_INSET_PX),
    y: Math.max(0, viewportH - elementH - DEFAULT_INSET_PX),
  };
}

/**
 * Parse a stored localStorage blob. Accepts the v1 shape only — anything else
 * (legacy values, corrupt JSON) returns null so callers fall back to the
 * default position.
 */
export function parseStoredPosition(raw: string | null): StoredBugReportDockPosition | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredBugReportDockPosition>;
    if (parsed.v !== 1) return null;
    if (typeof parsed.x !== "number" || Number.isNaN(parsed.x)) return null;
    if (typeof parsed.y !== "number" || Number.isNaN(parsed.y)) return null;
    if (typeof parsed.vw !== "number" || Number.isNaN(parsed.vw)) return null;
    if (typeof parsed.vh !== "number" || Number.isNaN(parsed.vh)) return null;
    return { v: 1, x: parsed.x, y: parsed.y, vw: parsed.vw, vh: parsed.vh };
  } catch {
    return null;
  }
}
